import { type FC, memo, type PropsWithChildren } from "react";
import Pagination from "./common/Pagination";
import { NoSearchResults } from "./common/NoSearchResults";
import { useDispatch, useSelector } from "react-redux";
import {
  selectContentPage,
  selectContentPageAmount,
  selectContentResultAmount,
  selectContentResults,
  selectIsInFlight,
} from "../redux/searchSelectors";
import { changeContentResultsPage } from "../redux/actions";
import NotFoundContactUsBar from "~/features/search/components/common/NotFoundContactUsBar/NotFoundContactUsBar";
import { Card } from "~/features/configurator/Linear/components/Card";
import { type ContentDataItem } from "~/utils/models";
import { type BreadcrumbItem } from "~/features/breadcrumbs/components/NavigationBreadcrumbs";
import { ArrowLink, Link, LinkType } from "../../../common/components/Link";
import { useIsMinTabletSize } from "../../../common/hooks/useMediaQuery";
import parseTruncate from "~/common/utils/parseTruncate";
import { useTranslation } from "react-i18next";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { styled } from "~/common/mui";

const ContentResults = () => {
  const resultAmount = useSelector(selectContentResultAmount);
  const currentContentPage = useSelector(selectContentPage);
  const pageAmount = useSelector(selectContentPageAmount);
  const results = useSelector(selectContentResults);

  const inFlight = useSelector(selectIsInFlight);

  const dispatch = useDispatch();
  const onPageChanged = (newPageNumber: number) => dispatch(changeContentResultsPage(newPageNumber));

  return (
    <>
      {!inFlight && (
        <>
          {resultAmount > 0 && (
            <>
              <ContentResultsList className={"scha__srp__content-results__list"}>
                {results.map((item) => (
                  <ContentResultsListItem className={"scha__srp__content-results__list-item"}>
                    <ContentResultItem item={item} />
                  </ContentResultsListItem>
                ))}
              </ContentResultsList>
              {pageAmount > 1 && (
                <ContentResultsPagination className="scha__srp__pagination">
                  <Pagination
                    pageAmount={pageAmount}
                    currentPage={currentContentPage}
                    onPageChange={onPageChanged}
                    renderHrefs={true}
                    goToIdAfterChange={"#resultsHeader"}
                  />
                </ContentResultsPagination>
              )}
            </>
          )}
          {resultAmount === 0 && <NoSearchResults />}
          <NotFoundDisclosure className="scha__srp__not-found-disc" />
        </>
      )}
    </>
  );
};

export default memo(ContentResults);

const ContentResultItem = memo(({ item }: { item: ContentDataItem }) => {
  const showBreadcrumbs = useIsMinTabletSize();
  const truncateTexts = !showBreadcrumbs;

  const titleParams = truncateTexts
    ? { children: parseTruncate(item.title, 30, "...") }
    : { dangerouslySetInnerHTML: { __html: item.title } };
  const contentParams = truncateTexts
    ? { children: parseTruncate(item.content, 130, "...") }
    : { dangerouslySetInnerHTML: { __html: item.content } };

  return (
    <Card className={"p-3"} disableHover={true}>
      <Link variant={LinkType.SECONDARY} href={item.label} className={"d-flex flex-direction-column flex-align-start"}>
        <p className={"title-large f-bold my-0"} {...titleParams} />
        <p className={"label-large mt-2 mb-0"} {...contentParams} />
      </Link>
      {showBreadcrumbs && item.breadcrumbs && <Breadcrumbs items={item.breadcrumbs} />}
    </Card>
  );
});

const Breadcrumbs = ({ items }: { items: BreadcrumbItem[] }) => {
  return (
    <BreadcrumbsList className="scha__srp__content-results__list-item__breadcrumb">
      {items.map((item) => (
        <Breadcrumb item={item} />
      ))}
    </BreadcrumbsList>
  );
};

const ContentResultsList = styled("ul")(({ theme }) => ({
  listStyle: "none",
  paddingLeft: 0,
  marginTop: theme.spacing(3.75),
}));

const ContentResultsListItem = styled("li")(({ theme }) => ({
  marginBottom: theme.spacing(2.5),
}));

const ContentResultsPagination = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(4),
}));

const NotFoundDisclosure = styled(NotFoundContactUsBar)(({ theme }) => ({
  marginTop: theme.spacing(4),
}));

const BreadcrumbsList = styled("ol")(({ theme }) => ({
  marginTop: theme.spacing(2),
  display: "flex",
  gap: theme.spacing(0, 1),
  flexWrap: "wrap",
  listStyle: "none",
  padding: theme.spacing(0, 0, 0, 2),
  ...theme.typography.bodyMedium,
  "& > *:first-of-type": {
    marginLeft: theme.spacing(-2),
  },
  ".scha__link__icon, .scha__link__icon--before": {
    margin: theme.spacing(0, 0.25, 0, 0),
  },
  ".scha__link--arrow-link": {
    ...theme.typography.bodyMedium,
    transform: "none",
    margin: 0,
  },
}));

const Breadcrumb: FC<{ item: BreadcrumbItem; isActive?: boolean }> = ({ item: { name, url } }) => {
  const { t } = useTranslation();
  // We should not try to translate the search query or designations that contain ":"
  name = name ?? "";
  const label = url.includes("search?text") ? name : name.includes(":") ? name : t(name);

  if (url) {
    return (
      <li>
        <ArrowLink href={url} variant={LinkType.NAVIGATION} className={"scha__link--content-bc"}>
          {label}
        </ArrowLink>
      </li>
    );
  }
  return (
    <li>
      <FakeArrowLink>{label}</FakeArrowLink>
    </li>
  );
};

const FakeArrowLink = ({ children }: PropsWithChildren) => {
  return (
    <div className={"scha__link disabled scha__link--content-bc"}>
      <span className="scha__link__icon scha__link__icon--before">
        <KeyboardArrowRightIcon className={"scha__link--arrow-link"}  />
      </span>
      {children}
    </div>
  );
};
